import React, { useState, useEffect, useContext, useCallback, useMemo } from 'react';
import { AppContext } from '@/App';
import axios from 'axios';
import {
  Package, Search, AlertCircle, RefreshCw, X, Minus, Loader2,
  Layers, Boxes, Wrench, Download, AlertTriangle, History
} from 'lucide-react';
import { toast } from 'sonner';

/**
 * Mirrors Stores Consumable Stock + Service Consumption but scoped to
 * project_consumable_stock_lots / project_material_issues. Tabs:
 *  - Stock (default): per-commodity available qty
 *  - Consumption: chronological list of issues
 */
export default function ProjectConsumption({ poType = 'material' }) {
  const { API } = useContext(AppContext);
  const isService = poType === 'service';
  const pageLabel = isService ? 'Project Service Consumption' : 'Project Material Consumption';
  const accent = isService ? 'indigo' : 'teal';

  const [stockData, setStockData] = useState([]);
  const [issueHistory, setIssueHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [tab, setTab] = useState('stock');

  const [showIssueModal, setShowIssueModal] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);
  const [issueForm, setIssueForm] = useState({
    quantity: '', issuedTo: '', department: '', purpose: ''
  });
  const [issuing, setIssuing] = useState(false);

  const fetchStock = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/projects/consumable-stock?poType=${poType}&limit=1000`);
      setStockData(r.data?.data || []);
    } catch (e) {
      toast.error('Failed to load Project Stock');
    } finally {
      setLoading(false);
    }
  }, [API, poType]);

  const fetchConsumption = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/projects/consumable-stock/consumption?poType=${poType}`);
      setIssueHistory(r.data?.data || []);
    } catch (e) {
      console.error(e);
    }
  }, [API, poType]);

  useEffect(() => {
    fetchStock();
    fetchConsumption();
  }, [fetchStock, fetchConsumption]);

  const openIssueModal = (item) => {
    setSelectedItem(item);
    setIssueForm({ quantity: '', issuedTo: '', department: '', purpose: '' });
    setShowIssueModal(true);
  };

  const submitIssue = async (e) => {
    e.preventDefault();
    const qty = parseFloat(issueForm.quantity);
    if (!qty || qty <= 0) {
      toast.error('Enter a valid quantity');
      return;
    }
    if (qty > selectedItem.availableQty) {
      toast.error(`Only ${selectedItem.availableQty} ${selectedItem.unit} available`);
      return;
    }
    if (!issueForm.issuedTo) {
      toast.error('Issued To is required');
      return;
    }
    setIssuing(true);
    try {
      await axios.post(`${API}/projects/consumable-stock/issue`, {
        commodityName: selectedItem.itemName,
        quantity: qty,
        uom: selectedItem.unit,
        issuedTo: issueForm.issuedTo,
        department: issueForm.department,
        purpose: issueForm.purpose,
        poType,
      });
      toast.success(`${qty} ${selectedItem.unit} ${isService ? 'consumed' : 'issued'}`);
      setShowIssueModal(false);
      await fetchStock();
      await fetchConsumption();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to issue');
    } finally {
      setIssuing(false);
    }
  };

  const filteredStock = useMemo(() => {
    const s = searchTerm.trim().toLowerCase();
    if (!s) return stockData;
    return stockData.filter(x =>
      (x.itemName || '').toLowerCase().includes(s) ||
      (x.vendorName || '').toLowerCase().includes(s) ||
      (x.lastGRN || '').toLowerCase().includes(s)
    );
  }, [stockData, searchTerm]);

  const filteredHistory = useMemo(() => {
    const s = searchTerm.trim().toLowerCase();
    if (!s) return issueHistory;
    return issueHistory.filter(x =>
      (x.commodityName || x.materialName || '').toLowerCase().includes(s) ||
      (x.issuedTo || '').toLowerCase().includes(s) ||
      (x.department || '').toLowerCase().includes(s) ||
      (x.purpose || '').toLowerCase().includes(s)
    );
  }, [issueHistory, searchTerm]);

  // Stats
  const stats = useMemo(() => ({
    totalCommodities: stockData.length,
    totalValue: stockData.reduce((s, x) => s + (x.value || 0), 0),
    outOfStock: stockData.filter(x => x.status === 'Out of Stock').length,
    phantomCount: stockData.filter(x => x.isPhantomConsumption).length,
  }), [stockData]);

  return (
    <div className="space-y-4" data-testid={`project-${poType}-consumption-page`}>
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            {isService ? <Wrench className={`text-${accent}-600`} /> : <Boxes className={`text-${accent}-600`} />}
            {pageLabel}
          </h1>
          <p className="text-sm text-gray-500 mt-1">
            {isService
              ? 'Service consumption against approved Project Service POs · fully isolated from Stores'
              : 'Material stock received via Project GRNs · fully isolated from Stores stock'}
          </p>
        </div>
        <button
          onClick={() => { fetchStock(); fetchConsumption(); }}
          className="px-3 py-2 bg-gray-100 hover:bg-gray-200 rounded-lg flex items-center gap-2 text-sm"
        >
          <RefreshCw size={14} /> Refresh
        </button>
      </div>

      {/* Stat cards */}
      <div className="grid grid-cols-4 gap-4">
        <div className="bg-white rounded-xl border border-gray-200 p-4">
          <p className="text-xs text-gray-500 uppercase">Commodities</p>
          <p className="text-2xl font-bold text-gray-900 mt-1">{stats.totalCommodities}</p>
        </div>
        <div className="bg-white rounded-xl border border-gray-200 p-4">
          <p className="text-xs text-gray-500 uppercase">Stock Value</p>
          <p className="text-2xl font-bold text-gray-900 mt-1">₹{stats.totalValue.toLocaleString('en-IN')}</p>
        </div>
        <div className="bg-white rounded-xl border border-gray-200 p-4">
          <p className="text-xs text-gray-500 uppercase">Out of Stock</p>
          <p className="text-2xl font-bold text-rose-600 mt-1">{stats.outOfStock}</p>
        </div>
        <div className={`bg-white rounded-xl border ${stats.phantomCount > 0 ? 'border-amber-300 bg-amber-50' : 'border-gray-200'} p-4`}>
          <p className="text-xs text-gray-500 uppercase flex items-center gap-1">
            {stats.phantomCount > 0 && <AlertTriangle size={12} className="text-amber-600" />}
            Phantom Issues
          </p>
          <p className={`text-2xl font-bold mt-1 ${stats.phantomCount > 0 ? 'text-amber-700' : 'text-gray-900'}`}>
            {stats.phantomCount}
          </p>
        </div>
      </div>

      {/* Tabs */}
      <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
        <div className="flex border-b border-gray-200 bg-gray-50">
          <button
            onClick={() => setTab('stock')}
            className={`flex items-center gap-2 px-6 py-3 text-sm font-medium border-b-2 transition ${
              tab === 'stock'
                ? `text-${accent}-700 border-${accent}-600 bg-white`
                : 'text-gray-600 border-transparent hover:bg-gray-100'
            }`}
            data-testid={`project-${poType}-tab-stock`}
          >
            <Layers size={16} /> Stock ({stockData.length})
          </button>
          <button
            onClick={() => setTab('consumption')}
            className={`flex items-center gap-2 px-6 py-3 text-sm font-medium border-b-2 transition ${
              tab === 'consumption'
                ? `text-${accent}-700 border-${accent}-600 bg-white`
                : 'text-gray-600 border-transparent hover:bg-gray-100'
            }`}
            data-testid={`project-${poType}-tab-consumption`}
          >
            <History size={16} /> {isService ? 'Service' : 'Material'} Consumption ({issueHistory.length})
          </button>
        </div>

        <div className="p-4">
          <div className="relative mb-3">
            <Search size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
            <input
              type="text"
              placeholder={tab === 'stock' ? 'Search commodity / vendor / GRN' : 'Search commodity / issued-to / purpose'}
              value={searchTerm}
              onChange={e => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-lg text-sm"
            />
          </div>

          {tab === 'stock' ? (
            loading ? (
              <div className="p-12 text-center text-gray-400"><Loader2 className="animate-spin mx-auto mb-2" /> Loading…</div>
            ) : filteredStock.length === 0 ? (
              <div className="p-12 text-center text-gray-500">
                <Package className="mx-auto mb-2 text-gray-300" size={36} />
                No project {isService ? 'service' : 'material'} stock yet. Approve a Project QA to create stock entries.
              </div>
            ) : (
              <table className="w-full text-sm">
                <thead className="bg-gray-50 border-b border-gray-200 text-left text-xs uppercase text-gray-600">
                  <tr>
                    <th className="px-3 py-2">Commodity</th>
                    <th className="px-3 py-2 text-right">Received</th>
                    <th className="px-3 py-2 text-right">Consumed</th>
                    <th className="px-3 py-2 text-right">Available</th>
                    <th className="px-3 py-2 text-right">Rate</th>
                    <th className="px-3 py-2 text-right">Value</th>
                    <th className="px-3 py-2">Last GRN</th>
                    <th className="px-3 py-2 text-center">Action</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {filteredStock.map(item => (
                    <tr key={item.id} className={`hover:bg-gray-50 ${item.isPhantomConsumption ? 'bg-amber-50' : ''}`} data-testid={`project-stock-row-${item.id}`}>
                      <td className="px-3 py-2">
                        <div className="font-medium text-gray-900">{item.itemName}</div>
                        {item.isPhantomConsumption && (
                          <div className="text-xs text-amber-700 flex items-center gap-1 mt-0.5">
                            <AlertTriangle size={11} /> Phantom: consumed without lot
                          </div>
                        )}
                      </td>
                      <td className="px-3 py-2 text-right text-gray-700">{item.totalReceived} {item.unit}</td>
                      <td className="px-3 py-2 text-right text-gray-700">{item.consumed}</td>
                      <td className={`px-3 py-2 text-right font-semibold ${item.availableQty > 0 ? 'text-emerald-700' : 'text-rose-600'}`}>{item.availableQty}</td>
                      <td className="px-3 py-2 text-right text-gray-700">₹{item.rate.toLocaleString('en-IN')}</td>
                      <td className="px-3 py-2 text-right text-gray-700">₹{(item.value || 0).toLocaleString('en-IN')}</td>
                      <td className="px-3 py-2 text-xs text-gray-500">
                        <div>{item.lastGRN || '-'}</div>
                        {item.vendorName && <div className="text-[10px]">{item.vendorName}</div>}
                      </td>
                      <td className="px-3 py-2 text-center">
                        <button
                          onClick={() => openIssueModal(item)}
                          disabled={item.availableQty <= 0}
                          className={`px-3 py-1 text-xs font-medium rounded-lg disabled:opacity-40 ${
                            isService
                              ? 'bg-indigo-600 text-white hover:bg-indigo-700'
                              : 'bg-teal-600 text-white hover:bg-teal-700'
                          }`}
                          data-testid={`project-stock-issue-${item.id}`}
                        >
                          {isService ? 'Consume' : 'Issue'}
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )
          ) : (
            filteredHistory.length === 0 ? (
              <div className="p-12 text-center text-gray-500">
                <History className="mx-auto mb-2 text-gray-300" size={36} />
                No {isService ? 'service consumption' : 'material issue'} records yet.
              </div>
            ) : (
              <table className="w-full text-sm">
                <thead className="bg-gray-50 border-b border-gray-200 text-left text-xs uppercase text-gray-600">
                  <tr>
                    <th className="px-3 py-2">Issue #</th>
                    <th className="px-3 py-2">Date</th>
                    <th className="px-3 py-2">Commodity</th>
                    <th className="px-3 py-2 text-right">Qty</th>
                    <th className="px-3 py-2">{isService ? 'Consumed By' : 'Issued To'}</th>
                    <th className="px-3 py-2">Department</th>
                    <th className="px-3 py-2">Purpose</th>
                    <th className="px-3 py-2 text-right">Value</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {filteredHistory.map(h => (
                    <tr key={h.id} className="hover:bg-gray-50">
                      <td className="px-3 py-2 font-mono text-xs">{h.id}</td>
                      <td className="px-3 py-2 text-xs text-gray-600">{h.date}</td>
                      <td className="px-3 py-2 font-medium text-gray-900">{h.commodityName || h.materialName}</td>
                      <td className="px-3 py-2 text-right">{h.quantity} {h.unit || h.uom}</td>
                      <td className="px-3 py-2 text-gray-700">{h.issuedTo}</td>
                      <td className="px-3 py-2 text-gray-700">{h.department || '-'}</td>
                      <td className="px-3 py-2 text-gray-700 text-xs max-w-[200px] truncate" title={h.purpose}>{h.purpose || '-'}</td>
                      <td className="px-3 py-2 text-right text-gray-700">₹{(h.value || 0).toLocaleString('en-IN')}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )
          )}
        </div>
      </div>

      {/* Issue Modal */}
      {showIssueModal && selectedItem && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <form onSubmit={submitIssue} className="bg-white rounded-2xl shadow-2xl w-full max-w-md">
            <div className={`p-5 border-b border-gray-200 ${isService ? 'bg-indigo-600' : 'bg-teal-600'} rounded-t-2xl`}>
              <h2 className="text-lg font-bold text-white">
                {isService ? 'Consume Service' : 'Issue Material'}: {selectedItem.itemName}
              </h2>
              <p className="text-xs text-white/80 mt-1">Available: {selectedItem.availableQty} {selectedItem.unit}</p>
            </div>
            <div className="p-5 space-y-3">
              <div>
                <label className="text-xs font-medium text-gray-600 uppercase">Quantity *</label>
                <input
                  type="number"
                  step="0.01"
                  min="0.01"
                  max={selectedItem.availableQty}
                  required
                  value={issueForm.quantity}
                  onChange={e => setIssueForm(f => ({ ...f, quantity: e.target.value }))}
                  className="mt-1 w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                  data-testid="project-issue-qty-input"
                />
              </div>
              <div>
                <label className="text-xs font-medium text-gray-600 uppercase">{isService ? 'Consumed By' : 'Issued To'} *</label>
                <input
                  type="text"
                  required
                  value={issueForm.issuedTo}
                  onChange={e => setIssueForm(f => ({ ...f, issuedTo: e.target.value }))}
                  className="mt-1 w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                  data-testid="project-issue-to-input"
                />
              </div>
              <div>
                <label className="text-xs font-medium text-gray-600 uppercase">Department</label>
                <input
                  type="text"
                  value={issueForm.department}
                  onChange={e => setIssueForm(f => ({ ...f, department: e.target.value }))}
                  className="mt-1 w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                />
              </div>
              <div>
                <label className="text-xs font-medium text-gray-600 uppercase">Purpose</label>
                <textarea
                  rows={2}
                  value={issueForm.purpose}
                  onChange={e => setIssueForm(f => ({ ...f, purpose: e.target.value }))}
                  className="mt-1 w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                />
              </div>
            </div>
            <div className="p-4 border-t border-gray-200 bg-gray-50 rounded-b-2xl flex justify-end gap-2">
              <button type="button" onClick={() => setShowIssueModal(false)} className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg">Cancel</button>
              <button
                type="submit"
                disabled={issuing}
                className={`flex items-center gap-2 px-5 py-2 text-white rounded-lg ${isService ? 'bg-indigo-600 hover:bg-indigo-700' : 'bg-teal-600 hover:bg-teal-700'} disabled:opacity-60`}
                data-testid="project-issue-submit-btn"
              >
                {issuing ? <Loader2 size={16} className="animate-spin" /> : <Minus size={16} />}
                {issuing ? 'Issuing…' : (isService ? 'Consume' : 'Issue')}
              </button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
}
